import React, { useState } from "react";
import { PageHeader } from "../components/PageHeader";
import { useTheme } from "../components/ThemeContext";
import SEO from "../components/SEO";
import GradientText from "@/components/GradientText";
import ShinyText from "@/components/reactbits/ShinyText";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { MagicCard } from "@/components/ui/magic-card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { submitN8nForm } from "@/services/n8nService";
import { APP_STORE_LINK, GOOGLE_PLAY_LINK } from "@/config/constants";
import {
  HelpCircle,
  MessageSquare,
  Building2,
  User,
  Smartphone,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
} from "lucide-react";

export function HelpPage() {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState<"patient" | "provider">("patient");

  // Patient Support State
  const [patientData, setPatientData] = useState({
    name: "",
    email: "",
    clinic_name: "",
    topic: "I need an invite code",
    message: "",
  });
  const [patientStatus, setPatientStatus] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });
  const [patientLoading, setPatientLoading] = useState(false);

  // Provider Support State
  const [providerData, setProviderData] = useState({
    name: "",
    email: "",
    phone: "",
    organization_name: "",
    topic: "Clinic Onboarding & Staff Setup",
    message: "",
  });
  const [providerStatus, setProviderStatus] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });
  const [providerLoading, setProviderLoading] = useState(false);

  const handlePatientSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPatientLoading(true);
    setPatientStatus({ type: null, message: "" });

    try {
      const result = await submitN8nForm({
        type: "support",
        source: "clinic_contact_support",
        name: patientData.name.trim() || null,
        email: patientData.email.trim() || null,
        clinic_name: patientData.clinic_name.trim() || null,
        topic: patientData.topic,
        message: patientData.message.trim() || patientData.topic,
      });

      if (result.success) {
        setPatientStatus({
          type: "success",
          message: "Thank you! Your support request has been received. Our team will contact you shortly.",
        });
        setPatientData({
          name: "",
          email: "",
          clinic_name: "",
          topic: "I need an invite code",
          message: "",
        });
      } else {
        setPatientStatus({
          type: "error",
          message: result.error || "Failed to submit support request. Please try again.",
        });
      }
    } catch (err: any) {
      setPatientStatus({
        type: "error",
        message: "An unexpected error occurred. Please try again.",
      });
    } finally {
      setPatientLoading(false);
    }
  };

  const handleProviderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProviderLoading(true);
    setProviderStatus({ type: null, message: "" });

    try {
      const result = await submitN8nForm({
        type: "support",
        source: "clinic_contact_support",
        name: providerData.name.trim() || null,
        email: providerData.email.trim() || null,
        phone: providerData.phone.trim() || null,
        organization_name: providerData.organization_name.trim() || null,
        clinic_name: providerData.organization_name.trim() || null,
        topic: providerData.topic,
        message: providerData.message.trim() || providerData.topic,
      });

      if (result.success) {
        setProviderStatus({
          type: "success",
          message: "Thank you! We've received your inquiry and our clinic support team will be in touch shortly.",
        });
        setProviderData({
          name: "",
          email: "",
          phone: "",
          organization_name: "",
          topic: "Clinic Onboarding & Staff Setup",
          message: "",
        });
      } else {
        setProviderStatus({
          type: "error",
          message: result.error || "Failed to submit request. Please try again.",
        });
      }
    } catch (err: any) {
      setProviderStatus({
        type: "error",
        message: "An unexpected error occurred. Please try again.",
      });
    } finally {
      setProviderLoading(false);
    }
  };

  const navItems = [
    { label: "For You", href: "/" },
    { label: "For Providers", href: "/healthcare" },
  ];

  return (
    <div className={theme === "dark" ? "bg-transparent text-white" : "bg-transparent text-slate-900"}>
      <SEO
        title="Help & Support | Quilt Wellness"
        description="Get assistance with Quilt, find invite code help, submit clinic inquiries, or browse our FAQ knowledge base."
        path="/help"
        noindex
      />
      <PageHeader navItems={navItems} />

      <main className="w-full flex flex-col items-center pt-32 pb-24 px-4 sm:px-6 max-w-6xl mx-auto">
        {/* 1. Hero Header */}
        <section className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-purple-500/30 bg-purple-500/10 text-purple-600 dark:text-purple-300 text-sm font-medium mb-6">
            <HelpCircle className="w-4 h-4" />
            <ShinyText text="Quilt Support Center" speed={4} />
          </div>

          <h1 className="text-4xl md:text-6xl font-light tracking-tight mb-6 leading-tight">
            How can we{" "}
            <GradientText
              colors={["#a855f7", "#6366f1", "#3b82f6", "#a855f7"]}
              animationSpeed={3}
              showBorder={false}
              className="font-bold mx-auto"
            >
              help you
            </GradientText>{" "}
            today?
          </h1>

          <p className="text-lg md:text-xl text-slate-600 dark:text-slate-400 font-light leading-relaxed">
            Whether you are a patient navigating daily micro-experiences or a healthcare provider managing patient engagement, we're here to assist.
          </p>
        </section>

        {/* 2. Quick Action Cards */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full mb-16">
          <div
            onClick={() => {
              setActiveTab("patient");
              document.getElementById("support-form-section")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="cursor-pointer group p-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/60 backdrop-blur-md hover:border-purple-500/50 hover:shadow-xl hover:shadow-purple-500/10 transition-all"
          >
            <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <User className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Patient Assistance</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 font-light leading-relaxed">
              Need an invite code, account access, or clinic linking help? Contact support directly.
            </p>
          </div>

          <div
            onClick={() => {
              setActiveTab("provider");
              document.getElementById("support-form-section")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="cursor-pointer group p-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/60 backdrop-blur-md hover:border-purple-500/50 hover:shadow-xl hover:shadow-purple-500/10 transition-all"
          >
            <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Building2 className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Provider & Clinic Support</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 font-light leading-relaxed">
              Questions about clinical workflows, batch generation, or database onboarding for your team.
            </p>
          </div>

          <div
            onClick={() => {
              document.getElementById("faq-section")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="cursor-pointer group p-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/60 backdrop-blur-md hover:border-purple-500/50 hover:shadow-xl hover:shadow-purple-500/10 transition-all"
          >
            <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-semibold mb-2">FAQs & Knowledge Base</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 font-light leading-relaxed">
              Quick answers to frequently asked questions regarding security, onboarding, and compatibility.
            </p>
          </div>
        </section>

        {/* 3. Interactive Support Form */}
        <section id="support-form-section" className="w-full max-w-3xl mx-auto mb-20">
          <Card className="border border-slate-200 dark:border-slate-800 shadow-2xl backdrop-blur-xl bg-white/90 dark:bg-slate-900/90 rounded-3xl overflow-hidden">
            <MagicCard
              className="p-0"
              gradientColor={theme === "dark" ? "#211639ff" : "#D9D9D955"}
              gradientFrom="#3b82f6"
              gradientTo="#9333ea"
              gradientSize={400}
              mode="gradient"
            >
              <CardHeader className="border-b border-slate-200 dark:border-slate-800 p-6 md:p-8">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <CardTitle className="text-2xl md:text-3xl font-medium">
                      Submit a Support Request
                    </CardTitle>
                    <CardDescription className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      Our support team will process your message and respond promptly.
                    </CardDescription>
                  </div>

                  {/* Tab Selector */}
                  <div className="flex bg-slate-100 dark:bg-zinc-950 p-1 rounded-xl border border-slate-200 dark:border-zinc-800 self-start sm:self-auto">
                    <button
                      type="button"
                      onClick={() => setActiveTab("patient")}
                      className={`px-4 py-2.5 md:py-2 min-h-11 md:min-h-0 rounded-lg text-sm font-medium transition-all ${
                        activeTab === "patient"
                          ? "bg-white dark:bg-purple-900/40 text-purple-600 dark:text-purple-300 shadow-sm"
                          : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                      }`}
                    >
                      Patient
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab("provider")}
                      className={`px-4 py-2.5 md:py-2 min-h-11 md:min-h-0 rounded-lg text-sm font-medium transition-all ${
                        activeTab === "provider"
                          ? "bg-white dark:bg-purple-900/40 text-purple-600 dark:text-purple-300 shadow-sm"
                          : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                      }`}
                    >
                      Healthcare Provider
                    </button>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-6 md:p-8">
                {activeTab === "patient" ? (
                  <form onSubmit={handlePatientSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="patient_name">Your Name *</Label>
                        <Input
                          id="patient_name"
                          type="text"
                          required
                          placeholder="Jane Doe"
                          value={patientData.name}
                          onChange={(e) =>
                            setPatientData((prev) => ({ ...prev, name: e.target.value }))
                          }
                          className="rounded-xl dark:bg-zinc-950/80"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="patient_email">Your Email *</Label>
                        <Input
                          id="patient_email"
                          type="email"
                          required
                          placeholder="jane@example.com"
                          value={patientData.email}
                          onChange={(e) =>
                            setPatientData((prev) => ({ ...prev, email: e.target.value }))
                          }
                          className="rounded-xl dark:bg-zinc-950/80"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="patient_clinic">Clinic / Healthcare Provider *</Label>
                        <Input
                          id="patient_clinic"
                          type="text"
                          required
                          placeholder="e.g. Avance Health, MD Psychiatry..."
                          value={patientData.clinic_name}
                          onChange={(e) =>
                            setPatientData((prev) => ({ ...prev, clinic_name: e.target.value }))
                          }
                          className="rounded-xl dark:bg-zinc-950/80"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="patient_topic">Topic / Reason *</Label>
                        <select
                          id="patient_topic"
                          value={patientData.topic}
                          onChange={(e) =>
                            setPatientData((prev) => ({ ...prev, topic: e.target.value }))
                          }
                          className="w-full h-11 md:h-10 px-3 py-2 text-base md:text-sm bg-white dark:bg-zinc-950/80 border border-slate-300 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="I need an invite code">I need an invite code</option>
                          <option value="Clinic Not Found">Clinic Not Found</option>
                          <option value="Login / Account Access Issue">Login / Account Access Issue</option>
                          <option value="App & Daily Content Question">App & Daily Content Question</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="patient_message">Message / Details *</Label>
                      <Textarea
                        id="patient_message"
                        rows={4}
                        required
                        placeholder="Please describe how we can assist you with your Quilt experience..."
                        value={patientData.message}
                        onChange={(e) =>
                          setPatientData((prev) => ({ ...prev, message: e.target.value }))
                        }
                        className="rounded-xl dark:bg-zinc-950/80"
                      />
                    </div>

                    {patientStatus.type && (
                      <div
                        className={`p-4 rounded-xl text-sm flex items-start gap-3 ${
                          patientStatus.type === "success"
                            ? "bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20"
                            : "bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20"
                        }`}
                      >
                        {patientStatus.type === "success" ? (
                          <CheckCircle2 className="w-5 h-5 shrink-0" />
                        ) : (
                          <AlertCircle className="w-5 h-5 shrink-0" />
                        )}
                        <span>{patientStatus.message}</span>
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={patientLoading}
                      className="w-full btn-primary text-white py-6 rounded-xl text-base font-medium shadow-lg shadow-purple-500/20"
                    >
                      {patientLoading ? "Submitting Request..." : "Submit Patient Request"}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleProviderSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="provider_name">Contact Name *</Label>
                        <Input
                          id="provider_name"
                          type="text"
                          required
                          placeholder="Dr. Alex Taylor"
                          value={providerData.name}
                          onChange={(e) =>
                            setProviderData((prev) => ({ ...prev, name: e.target.value }))
                          }
                          className="rounded-xl dark:bg-zinc-950/80"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="provider_email">Work Email *</Label>
                        <Input
                          id="provider_email"
                          type="email"
                          required
                          placeholder="alex@clinic.org"
                          value={providerData.email}
                          onChange={(e) =>
                            setProviderData((prev) => ({ ...prev, email: e.target.value }))
                          }
                          className="rounded-xl dark:bg-zinc-950/80"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="provider_org">Organization / Clinic Name *</Label>
                        <Input
                          id="provider_org"
                          type="text"
                          required
                          placeholder="Summit Behavioral Health"
                          value={providerData.organization_name}
                          onChange={(e) =>
                            setProviderData((prev) => ({ ...prev, organization_name: e.target.value }))
                          }
                          className="rounded-xl dark:bg-zinc-950/80"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="provider_phone">Phone Number</Label>
                        <Input
                          id="provider_phone"
                          type="tel"
                          placeholder="(555) 123-4567"
                          value={providerData.phone}
                          onChange={(e) =>
                            setProviderData((prev) => ({ ...prev, phone: e.target.value }))
                          }
                          className="rounded-xl dark:bg-zinc-950/80"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="provider_topic">Topic / Area of Inquiry *</Label>
                      <select
                        id="provider_topic"
                        value={providerData.topic}
                        onChange={(e) =>
                          setProviderData((prev) => ({ ...prev, topic: e.target.value }))
                        }
                        className="w-full h-11 md:h-10 px-3 py-2 text-base md:text-sm bg-white dark:bg-zinc-950/80 border border-slate-300 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="Clinic Onboarding & Staff Setup">Clinic Onboarding & Staff Setup</option>
                        <option value="Portal Technical Support">Portal Technical Support</option>
                        <option value="Content Generation & Scene Breakdown">Content Generation & Scene Breakdown</option>
                        <option value="EHR / Registry Integration">EHR / Registry Integration</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="provider_message">Message / Details *</Label>
                      <Textarea
                        id="provider_message"
                        rows={4}
                        required
                        placeholder="Tell us about your team's needs, questions, or integration requirements..."
                        value={providerData.message}
                        onChange={(e) =>
                          setProviderData((prev) => ({ ...prev, message: e.target.value }))
                        }
                        className="rounded-xl dark:bg-zinc-950/80"
                      />
                    </div>

                    {providerStatus.type && (
                      <div
                        className={`p-4 rounded-xl text-sm flex items-start gap-3 ${
                          providerStatus.type === "success"
                            ? "bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20"
                            : "bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20"
                        }`}
                      >
                        {providerStatus.type === "success" ? (
                          <CheckCircle2 className="w-5 h-5 shrink-0" />
                        ) : (
                          <AlertCircle className="w-5 h-5 shrink-0" />
                        )}
                        <span>{providerStatus.message}</span>
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={providerLoading}
                      className="w-full btn-primary text-white py-6 rounded-xl text-base font-medium shadow-lg shadow-purple-500/20"
                    >
                      {providerLoading ? "Submitting Inquiry..." : "Submit Provider Inquiry"}
                    </Button>
                  </form>
                )}
              </CardContent>
            </MagicCard>
          </Card>
        </section>

        {/* 4. Knowledge Base & FAQ Section */}
        <section id="faq-section" className="w-full max-w-4xl mx-auto mb-20">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-light mb-3">Frequently Asked Questions</h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              Quick answers regarding invite codes, account setup, and provider integration.
            </p>
          </div>

          <Card className="p-6 md:p-8 border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/50 backdrop-blur-md rounded-2xl">
            <Accordion type="single" collapsible className="w-full">
              {[
                {
                  question: "How do I get an invite code for Quilt?",
                  answer:
                    "Invite codes are distributed directly by participating healthcare providers and clinics. If your clinic is partnered with Quilt, check your email or ask your care team for your code. If your clinic isn't yet on Quilt, you can use our 'Clinic Not Found' form above to request an invitation on their behalf.",
                },
                {
                  question: "What if my clinic is not listed in the app?",
                  answer:
                    "You can easily submit your clinic's name and contact information through the support form or the interactive CTA on our home page. Our team will reach out to your provider to help them enroll.",
                },
                {
                  question: "Where can I download the Quilt mobile app?",
                  answer:
                    "Quilt is available for iOS on the Apple App Store and for Android on the Google Play Store. You will need an invite code to complete account registration after installation.",
                },
                {
                  question: "How can healthcare providers request a platform demo or get started?",
                  answer:
                    "Clinics and providers can click 'Schedule a Demo' or 'Get Started' anywhere on our Healthcare portal, or submit an inquiry using the Provider tab on this page. We'll set up a tailored walkthrough of our generative content engine and clinical oversight controls.",
                },
                {
                  question: "Is patient information safe and HIPAA-compliant?",
                  answer:
                    "Yes. Quilt is built with robust security practices, encrypted data pipelines, and strict clinical oversight controls. Clinical guidance is grounded exclusively in validated clinical knowledge bases.",
                },
              ].map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="border-slate-200 dark:border-slate-800"
                >
                  <AccordionTrigger className="text-left text-lg font-medium hover:no-underline text-slate-800 dark:text-slate-200 hover:text-purple-600 dark:hover:text-purple-400 transition-colors">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-slate-600 dark:text-slate-400 font-light leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Card>
        </section>

        {/* 5. Mobile App Download Links & Emergency Care Notice */}
        <section className="w-full max-w-4xl mx-auto space-y-6">
          <div className="p-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-zinc-950/60 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-1">
                Download the Quilt Wellness Companion
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-light">
                Available now for verified clinic patients on iOS and Android.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <a
                href={APP_STORE_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2.5 rounded-full text-sm font-semibold border border-slate-300 dark:border-zinc-700 hover:bg-slate-100 dark:hover:bg-zinc-800 transition-all inline-flex items-center gap-2"
              >
                App Store <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <a
                href={GOOGLE_PLAY_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2.5 rounded-full text-sm font-semibold border border-slate-300 dark:border-zinc-700 hover:bg-slate-100 dark:hover:bg-zinc-800 transition-all inline-flex items-center gap-2"
              >
                Google Play <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          <div className="p-4 rounded-xl border border-amber-500/20 bg-amber-500/10 text-amber-800 dark:text-amber-300 text-xs font-light text-center leading-relaxed">
            <strong className="font-semibold">Urgent Medical Notice:</strong> Quilt is a wellness companion and care coordination platform, not an emergency response service. If you are experiencing a medical or mental health emergency, please call <strong>911</strong> or the Suicide & Crisis Lifeline at <strong>988</strong> immediately.
          </div>
        </section>
      </main>
    </div>
  );
}

export default HelpPage;
